import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, Pause } from 'lucide-react';
import * as SliderPrimitive from '@radix-ui/react-slider';

const MinimalSlider = ({ value, onValueChange, ...props }) => (
  <SliderPrimitive.Root
    orientation="horizontal"
    value={value}
    onValueChange={onValueChange}
    className="relative flex w-28 h-3 touch-none select-none items-center justify-center"
    {...props}
  >
    {/* Thin Track */}
    <SliderPrimitive.Track className="relative w-full h-1 bg-gray-300 rounded-full overflow-hidden">
      {/* Animated Range Fill */}
      <SliderPrimitive.Range className="absolute h-full rounded-full bg-amber-500 transition-all duration-300 ease-out" />
    </SliderPrimitive.Track>

    {/* Thumb */}
    <SliderPrimitive.Thumb className="block h-3 w-3 cursor-pointer rounded-full border-2 border-amber-600 bg-white shadow-sm hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all" />
  </SliderPrimitive.Root>
);

// The <audio> element always stays mounted here (so music keeps its place through the theatre and
// room changes); the play button and volume slider are drawn into `slot`, a spot at the bottom of the
// top-right icon column (NavigationControls). With no slot (e.g. in the theatre) only the audio stays.
const AudioPlayer = ({ audioUrl, playSignal = 0, suspended = false, slot = null }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [wasPlayingBeforeHidden, setWasPlayingBeforeHidden] = useState(false);
  const [volume, setVolume] = useState(0.01);
  const [showVolume, setShowVolume] = useState(false);
  const audioRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => setShowVolume(false), 1500);
  };

  const handleMouseEnter = () => {
    clearTimeout(hideTimeoutRef.current);
    setShowVolume(true);
  };

  // const togglePlayPause = () => {
  //   if (audioRef.current) {
  //     if (isPlaying) audioRef.current.pause();
  //     else audioRef.current.play();
  //     setIsPlaying(!isPlaying);
  //   }
  // };
  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  // Pause while something else plays sound (theatre mode), then resume if it was playing.
  const resumeAfterSuspend = useRef(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (suspended) {
      resumeAfterSuspend.current = !audio.paused;
      audio.pause();
    } else if (resumeAfterSuspend.current) {
      resumeAfterSuspend.current = false;
      audio.play().catch(() => {});
    }
  }, [suspended]);

  // Start the music when asked from outside (e.g. the guided tour), at an audible volume.
  useEffect(() => {
    if (!playSignal || !audioRef.current) return;
    setVolume((v) => Math.max(v, 0.25));
    if (audioRef.current.paused) audioRef.current.play().catch(() => {});
  }, [playSignal]);

  const handleVolumeChange = (newVolume) => setVolume(newVolume[0]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (isPlaying && audioRef.current) {
          audioRef.current.pause();
          setWasPlayingBeforeHidden(true);
          setIsPlaying(false);
        }
      } else {
        if (wasPlayingBeforeHidden && audioRef.current) {
          audioRef.current.play();
          setIsPlaying(true);
          setWasPlayingBeforeHidden(false);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying, wasPlayingBeforeHidden]);

  const controls = (
    <div
      className={`relative flex items-center ${suspended ? 'hidden' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Volume Slider (pops out to the left of the button) */}
      <div
        className={`absolute right-12 top-1/2 -translate-y-1/2 transition-all duration-300 ease-in-out ${
          showVolume
            ? 'opacity-100 translate-x-0'
            : 'opacity-0 translate-x-4 pointer-events-none'
        }`}
      >
        <div className="bg-white/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center px-4 py-3">
          <MinimalSlider
            value={[volume]}
            onValueChange={handleVolumeChange}
            max={1}
            step={0.05}
          />
        </div>
      </div>

      {/* Play/Pause Button */}
      <button
        onClick={togglePlayPause}
        className="h-10 w-10 rounded-full flex items-center justify-center text-amber-800 bg-white/90 backdrop-blur-sm shadow-md hover:bg-amber-100 active:scale-95 transition-transform"
        title={isPlaying ? 'Pause music' : 'Play music'}
      >
        {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-1" />}
      </button>
    </div>
  );

  return (
    <>
      <audio
        ref={audioRef}
        src={audioUrl}
        loop
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      {slot && createPortal(controls, slot)}
    </>
  );
};

export default AudioPlayer;
